import { ChangeEvent, DragEvent, useRef, useState } from "react";
import { Button, Container, LinearProgress, Paper, Typography, alpha } from "@mui/material";
import { ArrowBack, Delete, DocumentScanner, InsertDriveFile } from "@mui/icons-material";
import clsx from "clsx";
import { makeStyles } from "@/compat/muiStyles";
import { useHistory } from "@/compat/router";
import { useSnackbar } from "notistack";
import CardBase from "../../../components/cards/CardBase";
import AiAssessmentReview from "../../components/riskassessments/AiAssessmentReview";
import RiskAssessmentNextStepsCard from "../../components/riskassessments/RiskAssessmentNextStepsCard";
import useScanRiskAssessmentDocument from "../../hooks/mutations/useScanRiskAssessmentDocument";
import useAddTemplate from "../../hooks/mutations/useAddTemplate";
import usePeople from "../../hooks/queries/usePeople";
import { buildAiTemplatePayload } from "../../utils/aiTemplatePayload";
import { useAppSelector } from "@/redux/hooks";
import { useAuth } from "../../../contexts/authContext";
import moduleAreas, { moduleUrls } from "../../constants/moduleAreaConstants";
import { baseRoute } from "../../routes";
import type { RiskAssessmentAiAssessmentDTO } from "../../types/riskAssessmentAiDtos";

const ACCEPTED_EXTENSIONS = [".pdf", ".docx"];
const MAX_BYTES = 25 * 1024 * 1024;

const useStyles = makeStyles((theme) => ({
    dropzone: {
        border: `2px dashed ${theme.palette.divider}`,
        borderRadius: theme.spacing(1.5),
        padding: theme.spacing(5, 2),
        textAlign: "center",
        cursor: "pointer",
        transition: "border-color .15s, background-color .15s",
        "&:hover": { borderColor: theme.palette.secondary.light },
    },
    dropzoneActive: {
        borderColor: theme.palette.secondary.main,
        backgroundColor: alpha(theme.palette.secondary.main, 0.04),
    },
    dropIcon: {
        width: 56,
        height: 56,
        borderRadius: theme.spacing(1.5),
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        margin: "0 auto",
        marginBottom: theme.spacing(2),
        backgroundColor: alpha(theme.palette.secondary.main, 0.12),
        color: theme.palette.secondary.main,
    },
    dropHint: { color: theme.palette.text.secondary, margin: theme.spacing(1, "auto", 2), maxWidth: 520 },
    hiddenInput: { display: "none" },
    chosen: { alignItems: "center", display: "flex", gap: theme.spacing(1.5), padding: theme.spacing(2) },
    chosenName: { flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
    working: { marginTop: theme.spacing(2) },
    workingNote: { color: theme.palette.text.secondary, display: "block", marginTop: theme.spacing(1) },
    disclaimer: { color: theme.palette.text.secondary, display: "block", marginTop: theme.spacing(2) },
    refused: {
        backgroundColor: alpha(theme.palette.warning.main, 0.08),
        borderLeft: `3px solid ${theme.palette.warning.main}`,
        borderRadius: theme.shape.borderRadius,
        marginTop: theme.spacing(2),
        padding: theme.spacing(1.5, 2),
    },
    footer: { display: "flex", justifyContent: "space-between", marginTop: theme.spacing(3) },
}));

export default function ScanRiskAssessment() {
    const classes = useStyles();
    const history = useHistory();
    const { enqueueSnackbar } = useSnackbar();
    const fileInputRef = useRef<HTMLInputElement>(null);

    const [file, setFile] = useState<File | null>(null);
    const [dragActive, setDragActive] = useState(false);
    const [assessment, setAssessment] = useState<RiskAssessmentAiAssessmentDTO | null>(null);
    const [conversationId, setConversationId] = useState<number | null>(null);
    const [refusal, setRefusal] = useState<string | null>(null);
    const [createdTemplate, setCreatedTemplate] = useState<{ id: number; name: string } | null>(null);

    const scan = useScanRiskAssessmentDocument();
    const addTemplate = useAddTemplate();
    const allPeople = usePeople();
    const { selectedSite, selectedAccount } = useAppSelector((state) => state.account);
    const { isWorknestUser } = useAuth();
    const raBase = `${baseRoute}/${moduleUrls[moduleAreas.RISKASSESSMENTS]}`;

    function chooseFile(incoming: FileList | File[]) {
        const candidate = Array.from(incoming)[0];
        if (!candidate) return;

        if (!ACCEPTED_EXTENSIONS.some((ext) => candidate.name.toLowerCase().endsWith(ext))) {
            enqueueSnackbar(`${candidate.name}: only PDF or Word documents can be scanned.`, { variant: "error" });
            return;
        }
        if (candidate.size > MAX_BYTES) {
            enqueueSnackbar(`${candidate.name}: exceeds the 25 MB limit.`, { variant: "error" });
            return;
        }

        setRefusal(null);
        setFile(candidate);
    }

    function onScan() {
        if (!file || scan.isLoading) return;

        setRefusal(null);
        scan.mutate(file, {
            onSuccess: (reply: any) => {
                setConversationId(reply?.conversationId ?? null);
                if (reply?.mode === "refused" || !reply?.assessment) {
                    setRefusal(
                        reply?.message ??
                            "That document doesn't look like a risk assessment, so there was nothing to build a template from."
                    );
                    return;
                }
                setAssessment(reply.assessment);
            },
            onError: (error: any) => {
                console.error(error);
                enqueueSnackbar(error?.title ?? error?.Message ?? "Could not scan that document.", { variant: "error" });
            },
        });
    }

    function onCreateTemplate() {
        if (!assessment) return;

        const payload = buildAiTemplatePayload(assessment, {
            conversationId,
            siteExternalId: isWorknestUser
                ? null
                : selectedSite?.externalId ?? selectedAccount?.account?.externalId ?? null,
            allPeople: allPeople?.data ?? [],
        });

        addTemplate.mutate(payload, {
            onSuccess: (result: any) =>
                setCreatedTemplate({ id: result?.templateId ?? result?.id, name: assessment.title }),
            onError: (error: any) => {
                console.error(error);
                const validation = error?.errors && Object.values(error.errors).flat().join(" ");
                enqueueSnackbar(validation ?? error?.title ?? "Could not create the template.", { variant: "error" });
            },
        });
    }

    function startOver() {
        setFile(null);
        setAssessment(null);
        setConversationId(null);
        setRefusal(null);
    }

    return (
        <Container>
            <CardBase
                title="Scan a Document"
                description={
                    assessment
                        ? "Check what has been read from your document, then create your template."
                        : "Point AI at a risk assessment you already have and it builds a reusable template from it."
                }
            >
                {createdTemplate ? (
                    <RiskAssessmentNextStepsCard
                        heading="Template created"
                        subtitle={`"${createdTemplate.name}" is saved to your templates. What would you like to do next?`}
                        note="You can create an assessment from this template at any time, for any site."
                        steps={[
                            {
                                label: "Create an assessment and complete it now",
                                onClick: () =>
                                    history.push(`${raBase}/add/template?templateId=${createdTemplate.id}&completeNow=true`),
                            },
                            {
                                label: "Add it to a site to complete later",
                                onClick: () => history.push(`${raBase}/add/template?templateId=${createdTemplate.id}`),
                            },
                            { label: "Just keep the template", variant: "text", onClick: () => history.push(raBase) },
                        ]}
                    />
                ) : assessment ? (
                    <AiAssessmentReview
                        assessment={assessment}
                        onChange={setAssessment}
                        onBack={startOver}
                        isSaving={addTemplate.isLoading}
                        onCreate={onCreateTemplate}
                    />
                ) : (
                    <>
                        {file ? (
                            <Paper variant="outlined" className={classes.chosen}>
                                <InsertDriveFile color="secondary" />
                                <div className={classes.chosenName}>
                                    <Typography variant="body2">{file.name}</Typography>
                                    <Typography variant="caption" color="textSecondary">
                                        {(file.size / (1024 * 1024)).toFixed(1)} MB
                                    </Typography>
                                </div>
                                <Button
                                    size="small"
                                    startIcon={<Delete />}
                                    disabled={scan.isLoading}
                                    onClick={startOver}
                                >
                                    Remove
                                </Button>
                            </Paper>
                        ) : (
                            <div
                                className={clsx(classes.dropzone, { [classes.dropzoneActive]: dragActive })}
                                onDragOver={(e: DragEvent<HTMLDivElement>) => {
                                    e.preventDefault();
                                    setDragActive(true);
                                }}
                                onDragLeave={() => setDragActive(false)}
                                onDrop={(e: DragEvent<HTMLDivElement>) => {
                                    e.preventDefault();
                                    setDragActive(false);
                                    if (e.dataTransfer?.files?.length) chooseFile(e.dataTransfer.files);
                                }}
                                onClick={() => fileInputRef.current?.click()}
                            >
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept=".pdf,.docx"
                                    className={classes.hiddenInput}
                                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                        if (e.target.files?.length) chooseFile(e.target.files);
                                        e.target.value = "";
                                    }}
                                />
                                <div className={classes.dropIcon}>
                                    <DocumentScanner />
                                </div>
                                <Typography variant="h6">Drag &amp; drop a risk assessment here</Typography>
                                <Typography variant="body2" className={classes.dropHint}>
                                    One document at a time. PDF and Word files are accepted, maximum 25 MB. The document
                                    is read to build your template and is not saved to the register.
                                </Typography>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={(e) => {
                                        // The dropzone itself opens the picker, so stop the
                                        // click bubbling or it opens twice.
                                        e.stopPropagation();
                                        fileInputRef.current?.click();
                                    }}
                                >
                                    Choose a document
                                </Button>
                            </div>
                        )}

                        {scan.isLoading && (
                            <div className={classes.working}>
                                <LinearProgress color="secondary" />
                                <Typography variant="caption" className={classes.workingNote}>
                                    Reading your document — this can take up to a minute.
                                </Typography>
                            </div>
                        )}

                        {refusal && (
                            <div className={classes.refused}>
                                <Typography variant="body2">{refusal}</Typography>
                            </div>
                        )}

                        <Typography variant="caption" className={classes.disclaimer}>
                            <strong>The AI only needs the hazards and controls.</strong> Assessments often name the
                            assessor or the people involved — avoid uploading documents that identify individuals or
                            record anyone's health information.
                        </Typography>
                        <Typography variant="caption" className={classes.disclaimer}>
                            The document is sent to our AI provider to be read, and is not retained by them. What comes
                            back is a draft — check it against your original before relying on it, and confirm the
                            content with your competent person.
                        </Typography>
                        <Typography variant="caption" className={classes.disclaimer}>
                            Usage is monitored to ensure fair use. AI assistance may be limited or withdrawn at any time.
                        </Typography>

                        <div className={classes.footer}>
                            <Button
                                variant="outlined"
                                color="primary"
                                startIcon={<ArrowBack />}
                                onClick={() => history.push(`${raBase}/add`)}
                            >
                                Back
                            </Button>
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<DocumentScanner />}
                                disabled={!file || scan.isLoading}
                                onClick={onScan}
                            >
                                {scan.isLoading ? "Scanning..." : "Scan document"}
                            </Button>
                        </div>
                    </>
                )}
            </CardBase>
        </Container>
    );
}
